import React, { useState, useEffect, useRef } from 'react';
import { Button, Switch, Select, InputNumber, Slider, Input } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import InputColor from 'react-input-color';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Smartphone, Monitor, ChevronDown, ChevronUp } from 'react-feather';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import { useNotification } from '../../context/NotificationContext';

const logoUrl = "https://netshine-assets.blr1.digitaloceanspaces.com/company_61/logos/company_logo_61_1751699306600";

const defaultConfig = {
  orgId: localStorage.getItem('companyid')
    ? CryptoJS.AES.encrypt(localStorage.getItem('companyid'), 'ns0-200200221').toString()
    : 'your-restaurant-id',
  colors: {
    primary: '#3B82F6',
    text: '#1f2937',
    background: '#f3f4f6',
    card: '#ffffff',
    muted: '#6b7280',
    buttonText: '#ffffff',
    buttonHover: '#2563eb',
    reservationButton: '#3B82F6',
    reservationButtonText: '#ffffff',
  },
  font: 'Inter, system-ui',
  headerFontSize: '1.8rem',
  showLogo: true,
  headerText: 'Reserve Your Table',
  showCompanyInfo: true,
  showMenuLink: false,
  showDigitalMenu: false,
  digitalMenuText: 'MENU',
  requiredFields: ['name', 'phone', 'guests'],
  showTimePicker: true,
  minGuests: 1,
  maxGuests: 10,
  allowSpecialRequests: true,
  allowInternalNotes: false,
  redirectAfterSubmission: '',
  autoConfirmation: true,
  confirmationMessage: 'Your reservation has been confirmed!',
  emailTemplate: 'Dear {name},\nYour reservation at {time} for {guests} guests is confirmed.',
  reminderHours: 24,
  maxPerSlot: 5,
  minAdvance: 2,
  maxAdvance: 30,
  bufferTime: 15,
  contactInfoContent: '<p>Phone: (123) 456-7890</p><p>Email: info@restaurant.com</p><p>Address: 123 Main St, City, Country</p>',
  reservationButtonText: 'Reserve Now',
};

// General Settings Component
const GeneralSettings = ({ config, expandedSections, toggleSection, handleChange, handleSliderChange, handleSelectChange }) => (
  <div className="card mb-4">
    <div
      className="card-header"
      style={{ backgroundColor: '#fff', padding: '12px 16px', borderBottom: 'none', position: 'relative', cursor: 'pointer' }}
      onClick={() => toggleSection('general')}
    >
      <h5 className="mb-0 d-flex justify-content-between align-items-center" style={{ fontSize: '14px', fontWeight: 600, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        General Settings
        {expandedSections.general ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </h5>
      <div style={{ position: 'absolute', bottom: '0', left: '0', width: '100%', height: '4px', backgroundColor: '#007bff' }} />
    </div>
    {expandedSections.general && (
      <div className="card-body" style={{ paddingLeft: '20px' }}>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="showLogo" className="col-md-4 col-form-label text-md-right text-nowrap">Show Logo</label>
            <div className="col-md-8 p-0">
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="toggle-showLogo"
                  name="showLogo"
                  checked={config.showLogo}
                  onChange={handleChange}
                  hidden
                />
                <label htmlFor="toggle-showLogo" className="toggle-label">
                  <span className="toggle-inner" />
                  <span className="toggle-circle" />
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="headerText" className="col-md-4 col-form-label text-md-right text-nowrap">Header Text</label>
            <div className="col-md-8 p-0">
              <input
                type="text"
                className="form-control"
                id="headerText"
                name="headerText"
                value={config.headerText}
                onChange={handleChange}
                placeholder="Reserve Your Table"
              />
            </div>
          </div>
        </div>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="headerFontSize" className="col-md-4 col-form-label text-md-right text-nowrap">Header Font Size</label>
            <div className="col-md-8 p-0">
              <Slider
                min={1}
                max={3}
                step={0.1}
                value={parseFloat(config.headerFontSize)}
                onChange={(value) => handleSliderChange('headerFontSize', value)}
                style={{ width: '100%' }}
              />
              <div style={{ textAlign: 'center', marginTop: '8px' }}>{config.headerFontSize}</div>
            </div>
          </div>
        </div>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="showCompanyInfo" className="col-md-4 col-form-label text-md-right text-nowrap">Show Company Info</label>
            <div className="col-md-8 p-0">
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="toggle-showCompanyInfo"
                  name="showCompanyInfo"
                  checked={config.showCompanyInfo}
                  onChange={handleChange}
                  hidden
                />
                <label htmlFor="toggle-showCompanyInfo" className="toggle-label">
                  <span className="toggle-inner" />
                  <span className="toggle-circle" />
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="showMenuLink" className="col-md-4 col-form-label text-md-right text-nowrap">Show Menu Link</label>
            <div className="col-md-8 p-0">
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="toggle-showMenuLink"
                  name="showMenuLink"
                  checked={config.showMenuLink}
                  onChange={handleChange}
                  hidden
                />
                <label htmlFor="toggle-showMenuLink" className="toggle-label">
                  <span className="toggle-inner" />
                  <span className="toggle-circle" />
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="font" className="col-md-4 col-form-label text-md-right text-nowrap">Font Family</label>
            <div className="col-md-8 p-0">
              <Select
                value={config.font}
                onChange={(value) => handleSelectChange('font', value)}
                style={{ width: '100%' }}
                options={[
                  { value: 'Inter, system-ui', label: 'Inter' },
                  { value: 'Arial, sans-serif', label: 'Arial' },
                  { value: 'Roboto, sans-serif', label: 'Roboto' },
                ]}
              />
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
);

// Color Settings Component
const ColorSettings = ({ config, expandedSections, toggleSection, handleColorChange }) => (
  <div className="card mb-4">
    <div
      className="card-header"
      style={{ backgroundColor: '#fff', padding: '12px 16px', borderBottom: 'none', position: 'relative', cursor: 'pointer' }}
      onClick={() => toggleSection('colors')}
    >
      <h5 className="mb-0 d-flex justify-content-between align-items-center" style={{ fontSize: '14px', fontWeight: 600, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        Colors
        {expandedSections.colors ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </h5>
      <div style={{ position: 'absolute', bottom: '0', left: '0', width: '100%', height: '4px', backgroundColor: '#007bff' }} />
    </div>
    {expandedSections.colors && (
      <div className="card-body" style={{ paddingLeft: '20px' }}>
        {[
  { name: 'primary', label: 'Primary Color' },
  { name: 'text', label: 'Text Color' },
  { name: 'background', label: 'Background Color' },
  { name: 'card', label: 'Card Background Color' },
  { name: 'muted', label: 'Muted Text Color' },
  { name: 'buttonText', label: 'Button Text Color' },
  { name: 'buttonHover', label: 'Button Hover Color' },
].map((field) => (
  <div className="form-group mb-3" key={field.name}>
    <div className="row align-items-center">
      <label htmlFor={field.name} className="col-md-4 col-form-label text-md-right text-nowrap">{field.label}</label>
      <div className="col-md-8 p-0">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="text"
            value={config.colors[field.name] || '#000000'}
            readOnly
            style={{
              border: '1px solid #ccc',
              borderRadius: '4px 0 0 4px',
              padding: '6px 8px',
              width: '80px',
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              textAlign: 'center',
            }}
          />
          <InputColor
            initialValue={config.colors[field.name] || '#000000'}
            onChange={(color) => handleColorChange(color, field.name)}
            placement="right"
            style={{
              width: '30px',
              height: '30px',
              border: '1px solid #ccc',
              borderRadius: '0 4px 4px 0',
              overflow: 'hidden',
              padding: 0,
              cursor: 'pointer',
              backgroundColor: config.colors[field.name] || '#000000',
            }}
          />
        </div>
      </div>
    </div>
  </div>
))}
      </div>
    )}
  </div>
);

// Reservation Button Settings Component
const ReservationButtonSettings = ({ config, expandedSections, toggleSection, handleChange, handleColorChange }) => (
  <div className="card mb-4">
    <div
      className="card-header"
      style={{ backgroundColor: '#fff', padding: '12px 16px', borderBottom: 'none', position: 'relative', cursor: 'pointer' }}
      onClick={() => toggleSection('reservationButton')}
    >
      <h5 className="mb-0 d-flex justify-content-between align-items-center" style={{ fontSize: '14px', fontWeight: 600, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        Reservation Button
        {expandedSections.reservationButton ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </h5>
      <div style={{ position: 'absolute', bottom: '0', left: '0', width: '100%', height: '4px', backgroundColor: '#007bff' }} />
    </div>
    {expandedSections.reservationButton && (
      <div className="card-body" style={{ paddingLeft: '20px' }}>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="reservationButtonText" className="col-md-4 col-form-label text-md-right text-nowrap">Button Text</label>
            <div className="col-md-8 p-0">
              <Input
                type="text"
                className="form-control"
                id="reservationButtonText"
                name="reservationButtonText"
                value={config.reservationButtonText || 'Reserve Now'}
                onChange={handleChange}
                placeholder="Reserve Now"
              />
            </div>
          </div>
        </div>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="reservationButton" className="col-md-4 col-form-label text-md-right text-nowrap">Button Color</label>
            <div className="col-md-8 p-0">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="text"
                  value={config.colors.reservationButton || '#3B82F6'}
                  readOnly
                  style={{
                    border: '1px solid #ccc',
                    borderRadius: '4px 0 0 4px',
                    padding: '6px 8px',
                    width: '80px',
                    fontFamily: 'monospace',
                    backgroundColor: '#f5f5f5',
                    textAlign: 'center',
                  }}
                />
                <InputColor
                  initialValue={config.colors.reservationButton || '#3B82F6'}
                  onChange={(color) => handleColorChange(color, 'reservationButton')}
                  placement="right"
                  style={{
                    width: '30px',
                    height: '30px',
                    border: '1px solid #ccc',
                    borderRadius: '0 4px 4px 0',
                    overflow: 'hidden',
                    padding: 0,
                    cursor: 'pointer',
                    backgroundColor: config.colors.reservationButton || '#3B82F6',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="reservationButtonTextColor" className="col-md-4 col-form-label text-md-right text-nowrap">Button Text Color</label>
            <div className="col-md-8 p-0">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="text"
                  value={config.colors.reservationButtonText || '#FFFFFF'}
                  readOnly
                  style={{
                    border: '1px solid #ccc',
                    borderRadius: '4px 0 0 4px',
                    padding: '6px 8px',
                    width: '80px',
                    fontFamily: 'monospace',
                    backgroundColor: '#f5f5f5',
                    textAlign: 'center',
                  }}
                />
                <InputColor
                  initialValue={config.colors.reservationButtonText || '#FFFFFF'}
                  onChange={(color) => handleColorChange(color, 'reservationButtonText')}
                  placement="right"
                  style={{
                    width: '30px',
                    height: '30px',
                    border: '1px solid #ccc',
                    borderRadius: '0 4px 4px 0',
                    overflow: 'hidden',
                    padding: 0,
                    cursor: 'pointer',
                    backgroundColor: config.colors.reservationButtonText || '#FFFFFF',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
);

// Layout Settings Component
const LayoutSettings = ({ config, expandedSections, toggleSection, handleChange }) => (
  <div className="card mb-4">
    <div
      className="card-header"
      style={{ backgroundColor: '#fff', padding: '12px 16px', borderBottom: 'none', position: 'relative', cursor: 'pointer' }}
      onClick={() => toggleSection('layout')}
    >
      <h5 className="mb-0 d-flex justify-content-between align-items-center" style={{ fontSize: '14px', fontWeight: 600, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        Layout Settings
        {expandedSections.layout ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </h5>
      <div style={{ position: 'absolute', bottom: '0', left: '0', width: '100%', height: '4px', backgroundColor: '#007bff' }} />
    </div>
    {expandedSections.layout && (
      <div className="card-body" style={{ paddingLeft: '20px' }}>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="showDigitalMenu" className="col-md-4 col-form-label text-md-right text-nowrap">Show Digital Menu Link</label>
            <div className="col-md-8 p-0">
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="toggle-showDigitalMenu"
                  name="showDigitalMenu"
                  checked={config.showDigitalMenu}
                  onChange={handleChange}
                  hidden
                />
                <label htmlFor="toggle-showDigitalMenu" className="toggle-label">
                  <span className="toggle-inner" />
                  <span className="toggle-circle" />
                </label>
              </div>
            </div>
          </div>
        </div>
        {config.showDigitalMenu && (
          <div className="form-group mb-3">
            <div className="row align-items-center">
              <label htmlFor="digitalMenuText" className="col-md-4 col-form-label text-md-right text-nowrap">Digital Menu Link Text</label>
              <div className="col-md-8 p-0">
                <input
                  type="text"
                  className="form-control"
                  id="digitalMenuText"
                  name="digitalMenuText"
                  value={config.digitalMenuText}
                  onChange={handleChange}
                  placeholder="e.g., MENU"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    )}
  </div>
);

// Contact Info Settings Component
const ContactInfoSettings = ({ config, expandedSections, toggleSection, handleContactInfoChange }) => (
  <div className="card mb-4">
    <div
      className="card-header"
      style={{ backgroundColor: '#fff', padding: '12px 16px', borderBottom: 'none', position: 'relative', cursor: 'pointer' }}
      onClick={() => toggleSection('contactInfo')}
    >
      <h5 className="mb-0 d-flex justify-content-between align-items-center" style={{ fontSize: '14px', fontWeight: 600, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        Contact Information
        {expandedSections.contactInfo ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </h5>
      <div style={{ position: 'absolute', bottom: '0', left: '0', width: '100%', height: '4px', backgroundColor: '#007bff' }} />
    </div>
    {expandedSections.contactInfo && (
      <div className="card-body" style={{ paddingLeft: '20px' }}>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="contactInfoContent" className="col-md-4 col-form-label text-md-right text-nowrap">Contact Details</label>
            <div className="col-md-8 p-0">
              <ReactQuill
                value={config.contactInfoContent}
                onChange={handleContactInfoChange}
                theme="snow"
                style={{ backgroundColor: '#fff' }}
              />
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
);

// Reservation Settings Component
const ReservationSettings = ({ config, expandedSections, toggleSection, handleChange, handleSelectChange, handleNumberChange }) => (
  <div className="card mb-4">
    <div
      className="card-header"
      style={{ backgroundColor: '#fff', padding: '12px 16px', borderBottom: 'none', position: 'relative', cursor: 'pointer' }}
      onClick={() => toggleSection('reservationSettings')}
    >
      <h5 className="mb-0 d-flex justify-content-between align-items-center" style={{ fontSize: '14px', fontWeight: 600, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        Reservation Settings
        {expandedSections.reservationSettings ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </h5>
      <div style={{ position: 'absolute', bottom: '0', left: '0', width: '100%', height: '4px', backgroundColor: '#007bff' }} />
    </div>
    {expandedSections.reservationSettings && (
      <div className="card-body" style={{ paddingLeft: '20px' }}>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="requiredFields" className="col-md-4 col-form-label text-md-right text-nowrap">Required Fields</label>
            <div className="col-md-8 p-0">
              <Select
                mode="multiple"
                value={config.requiredFields}
                onChange={(value) => handleSelectChange('requiredFields', value)}
                style={{ width: '100%' }}
                options={[
                  { value: 'name', label: 'Name' },
                  { value: 'email', label: 'Email' },
                  { value: 'phone', label: 'Phone' },
                  { value: 'guests', label: 'Guests' },
                  { value: 'specialRequest', label: 'Special Request' },
                ]}
              />
            </div>
          </div>
        </div>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="showTimePicker" className="col-md-4 col-form-label text-md-right text-nowrap">Show Time Picker</label>
            <div className="col-md-8 p-0">
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="toggle-showTimePicker"
                  name="showTimePicker"
                  checked={config.showTimePicker}
                  onChange={handleChange}
                  hidden
                />
                <label htmlFor="toggle-showTimePicker" className="toggle-label">
                  <span className="toggle-inner" />
                  <span className="toggle-circle" />
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label className="col-md-4 col-form-label text-md-right text-nowrap">Guest Count Control</label>
            <div className="col-md-8 p-0 d-flex">
              <InputNumber
                min={1}
                value={config.minGuests}
                onChange={(value) => handleNumberChange('minGuests', value)}
                placeholder="Min Guests"
                style={{ width: 'calc(50% - 8px)' }}
              />
              <InputNumber
                min={1}
                value={config.maxGuests}
                onChange={(value) => handleNumberChange('maxGuests', value)}
                placeholder="Max Guests"
                style={{ width: 'calc(50% - 8px)', marginLeft: '16px' }}
              />
            </div>
          </div>
        </div>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="allowSpecialRequests" className="col-md-4 col-form-label text-md-right text-nowrap">Allow Special Requests</label>
            <div className="col-md-8 p-0">
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="toggle-allowSpecialRequests"
                  name="allowSpecialRequests"
                  checked={config.allowSpecialRequests}
                  onChange={handleChange}
                  hidden
                />
                <label htmlFor="toggle-allowSpecialRequests" className="toggle-label">
                  <span className="toggle-inner" />
                  <span className="toggle-circle" />
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="allowInternalNotes" className="col-md-4 col-form-label text-md-right text-nowrap">Allow Internal Notes</label>
            <div className="col-md-8 p-0">
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="toggle-allowInternalNotes"
                  name="allowInternalNotes"
                  checked={config.allowInternalNotes}
                  onChange={handleChange}
                  hidden
                />
                <label htmlFor="toggle-allowInternalNotes" className="toggle-label">
                  <span className="toggle-inner" />
                  <span className="toggle-circle" />
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="redirectAfterSubmission" className="col-md-4 col-form-label text-md-right text-nowrap">Redirect After Submission</label>
            <div className="col-md-8 p-0">
              <input
                type="text"
                className="form-control"
                id="redirectAfterSubmission"
                name="redirectAfterSubmission"
                value={config.redirectAfterSubmission}
                onChange={handleChange}
                placeholder="Thank You page URL"
              />
            </div>
          </div>
        </div>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="autoConfirmation" className="col-md-4 col-form-label text-md-right text-nowrap">Auto Confirmation</label>
            <div className="col-md-8 p-0">
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="toggle-autoConfirmation"
                  name="autoConfirmation"
                  checked={config.autoConfirmation}
                  onChange={handleChange}
                  hidden
                />
                <label htmlFor="toggle-autoConfirmation" className="toggle-label">
                  <span className="toggle-inner" />
                  <span className="toggle-circle" />
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="confirmationMessage" className="col-md-4 col-form-label text-md-right text-nowrap">Confirmation Message</label>
            <div className="col-md-8 p-0">
              <textarea
                className="form-control"
                id="confirmationMessage"
                name="confirmationMessage"
                value={config.confirmationMessage}
                onChange={handleChange}
                rows={3}
                placeholder="Your reservation has been confirmed!"
              />
            </div>
          </div>
        </div>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="emailTemplate" className="col-md-4 col-form-label text-md-right text-nowrap">Email Confirmation Template</label>
            <div className="col-md-8 p-0">
              <ReactQuill
                value={config.emailTemplate}
                onChange={(value) => handleSelectChange('emailTemplate', value)}
                theme="snow"
                style={{ backgroundColor: '#fff' }}
              />
            </div>
          </div>
        </div>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="reminderHours" className="col-md-4 col-form-label text-md-right text-nowrap">Reminder Hours Before</label>
            <div className="col-md-8 p-0">
              <InputNumber
                min={0}
                value={config.reminderHours}
                onChange={(value) => handleNumberChange('reminderHours', value)}
                placeholder="24"
                style={{ width: '100%' }}
              />
            </div>
          </div>
        </div>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="maxPerSlot" className="col-md-4 col-form-label text-md-right text-nowrap">Max Reservations Per Slot</label>
            <div className="col-md-8 p-0">
              <InputNumber
                min={1}
                value={config.maxPerSlot}
                onChange={(value) => handleNumberChange('maxPerSlot', value)}
                placeholder="5"
                style={{ width: '100%' }}
              />
            </div>
          </div>
        </div>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="minAdvance" className="col-md-4 col-form-label text-md-right text-nowrap">Min Advance Booking (hours)</label>
            <div className="col-md-8 p-0">
              <InputNumber
                min={0}
                value={config.minAdvance}
                onChange={(value) => handleNumberChange('minAdvance', value)}
                placeholder="2"
                style={{ width: '100%' }}
              />
            </div>
          </div>
        </div>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="maxAdvance" className="col-md-4 col-form-label text-md-right text-nowrap">Max Advance Booking (days)</label>
            <div className="col-md-8 p-0">
              <InputNumber
                min={1}
                value={config.maxAdvance}
                onChange={(value) => handleNumberChange('maxAdvance', value)}
                placeholder="30"
                style={{ width: '100%' }}
              />
            </div>
          </div>
        </div>
        <div className="form-group mb-3">
          <div className="row align-items-center">
            <label htmlFor="bufferTime" className="col-md-4 col-form-label text-md-right text-nowrap">Buffer Time (minutes)</label>
            <div className="col-md-8 p-0">
              <InputNumber
                min={0}
                value={config.bufferTime}
                onChange={(value) => handleNumberChange('bufferTime', value)}
                placeholder="15"
                style={{ width: '100%' }}
              />
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
);

// Embed Code Section Component
const EmbedCodeSection = ({ expandedSections, toggleSection, generatedCode, copyToClipboard }) => (
  <div className="card mb-4">
    <div
      className="card-header"
      style={{ backgroundColor: '#fff', padding: '12px 16px', borderBottom: 'none', position: 'relative', cursor: 'pointer' }}
      onClick={() => toggleSection('embedCode')}
    >
      <h5 className="mb-0 d-flex justify-content-between align-items-center" style={{ fontSize: '14px', fontWeight: 600, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        Generated Embed Codes
        {expandedSections.embedCode ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </h5>
      <div style={{ position: 'absolute', bottom: '0', left: '0', width: '100%', height: '4px', backgroundColor: '#007bff' }} />
    </div>
    {expandedSections.embedCode && generatedCode.snippet && (
      <div className="card-body" style={{ paddingLeft: '20px' }}>
        <div className="form-group mb-3">
          <label className="form-label">JS Snippet (Recommended)</label>
          <pre style={{ background: '#f5f5f5', padding: 16, borderRadius: 4, overflowX: 'auto' }}>
            {generatedCode.snippet}
          </pre>
          <Button icon={<CopyOutlined />} onClick={() => copyToClipboard(generatedCode.snippet)}>Copy Snippet</Button>
        </div>
        <div className="form-group mb-3">
          <label className="form-label">Iframe (Simple Embed)</label>
          <pre style={{ background: '#f5f5f5', padding: 16, borderRadius: 4, overflowX: 'auto' }}>
            {generatedCode.iframe}
          </pre>
          <Button icon={<CopyOutlined />} onClick={() => copyToClipboard(generatedCode.iframe)}>Copy Iframe</Button>
        </div>
        <p className="text-muted">
          <strong>Instructions:</strong><br />
          1. Paste the JS Snippet or Iframe into your website's HTML.<br />
          2. Replace "https://yourdomain.com" with your actual hosted URL.<br />
          3. Test the widget on your site.<br />
          4. Customize further as needed.
        </p>
      </div>
    )}
  </div>
);

// Preview Section Component
const PreviewSection = ({ config, viewMode, setViewMode }) => {
  const mobileFrameStyle = {
    width: '375px',
    height: '667px',
    margin: '20px auto',
    border: '10px solid #000',
    borderRadius: '40px',
    overflow: 'hidden',
    boxShadow: '0 0 20px rgba(0, 0, 0, 0.5)',
    background: '#000',
    position: 'relative',
  };
  const previewStyle = {
    width: '100%',
    height: '100%',
    overflowY: 'auto',
    backgroundColor: config.colors.background,
    color: config.colors.text,
  };
  return (
    <div className="card" style={{ position: 'relative', height: '100%' }}>
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0 d-flex align-items-center">Configuration Preview</h5>
        <div>
          <button
            className="btn btn-outline-primary mx-2"
            onClick={() => setViewMode('desktop')}
            style={{ margin: '0 10px' }}
          >
            <Monitor size={16} /> Desktop
          </button>
          <button
            className="btn btn-outline-primary mx-2"
            onClick={() => setViewMode('mobile')}
            style={{ margin: '0 10px' }}
          >
            <Smartphone size={16} /> Mobile
          </button>
        </div>
      </div>
      <div className="card-body" style={{ maxHeight: '80vh', overflowY: 'auto', position: 'relative' }}>
        <div style={viewMode === 'mobile' ? mobileFrameStyle : {}}>
          <div style={previewStyle}>
            <style>{`
              :root {
                --primary: ${config.colors.primary};
                --text: ${config.colors.text};
                --background: ${config.colors.background};
                --card: ${config.colors.card};
                --muted: ${config.colors.muted};
                --button-text: ${config.colors.buttonText};
                --button-hover: ${config.colors.buttonHover};
                --reservation-button: ${config.colors.reservationButton || '#3B82F6'};
                --reservation-button-text: ${config.colors.reservationButtonText || '#FFFFFF'};
              }
              .reservation-widget-root {
                background: var(--background);
                color: var(--text);
                font-family: ${config.font};
                padding: 20px;
                border-radius: 8px;
                box-shadow: 0 4px 12px rgba(0,0,0,0.1);
              }
              .reservation-header {
                text-align: center;
                margin-bottom: 20px;
              }
              .reservation-logo {
                max-width: 150px;
                height: auto;
                margin: 0 auto;
              }
              .reservation-title {
                font-size: ${config.headerFontSize};
                color: var(--primary);
                font-weight: 600;
              }
              .reservation-form {
                display: flex;
                flex-direction: column;
                gap: 15px;
              }
              .reservation-form input, .reservation-form select, .reservation-form textarea {
                padding: 10px;
                border: 1px solid var(--primary);
                border-radius: 4px;
                background: var(--card);
                color: var(--text);
              }
              .reservation-form button {
                background: var(--primary);
                color: var(--button-text);
                padding: 10px;
                border: none;
                border-radius: 4px;
                font-weight: 600;
                cursor: pointer;
              }
              .reservation-form button:hover {
                background: var(--button-hover);
              }
              .reservation-footer {
                text-align: center;
                color: var(--muted);
                margin-top: 20px;
              }
              .reservation-contact {
                text-align: center;
                color: var(--muted);
                margin-bottom: 20px;
              }
              .standalone-reservation-button {
                position: fixed;
                top: 50%;
                right: 0;
                transform: translateY(-50%) rotate(-90deg);
                transform-origin: right bottom;
                background: var(--reservation-button);
                color: var(--reservation-button-text);
                padding: 15px 20px;
                font-size: 16px;
                font-weight: 600;
                border: none;
                border-radius: 6px 6px 0 0;
                cursor: pointer;
                z-index: 1500;
                box-shadow: 0 2px 6px rgba(0,0,0,0.2);
                white-space: nowrap;
                transition: background-color 0.2s ease;
              }
              .standalone-reservation-button:hover {
                background: var(--button-hover);
              }
            `}</style>
            <div className="reservation-widget-root">
              <header className="reservation-header">
                {config.showLogo && <img src={logoUrl} alt="Logo" className="reservation-logo" />}
                <h2 className="reservation-title">{config.headerText}</h2>
              </header>
              <div className="reservation-form">
                {config.requiredFields.includes('name') && <input type="text" placeholder="Name" />}
                {config.requiredFields.includes('email') && <input type="email" placeholder="Email" />}
                {config.requiredFields.includes('phone') && <input type="tel" placeholder="Phone" />}
                {config.requiredFields.includes('guests') && (
                  <select>
                    {[...Array(config.maxGuests - config.minGuests + 1)].map((_, i) => (
                      <option key={i} value={i + config.minGuests}>
                        {i + config.minGuests} Guest{i + config.minGuests > 1 ? 's' : ''}
                      </option>
                    ))}
                  </select>
                )}
                {config.showTimePicker && <input type="datetime-local" />}
                {config.allowSpecialRequests && <textarea placeholder="Special Requests" rows={3} />}
                <button>Submit Reservation</button>
              </div>
              {(config.showCompanyInfo || config.showMenuLink || config.showDigitalMenu) && (
                <footer className="reservation-footer">
                  {config.showCompanyInfo && config.contactInfoContent && (
                    <div className="reservation-contact" dangerouslySetInnerHTML={{ __html: config.contactInfoContent }} />
                  )}
                  {config.showMenuLink && <a href="#" style={{ marginRight: '10px' }}>View Menu</a>}
                  {config.showDigitalMenu && <a href="#">{config.digitalMenuText}</a>}
                </footer>
              )}
            </div>
          </div>
        </div>
        <button className="standalone-reservation-button">{config.reservationButtonText || 'Reserve Now'}</button>
      </div>
    </div>
  );
};

// Main WidgetGenerator Component
const WidgetGenerator = () => {
  const { notify } = useNotification();
  const [config, setConfig] = useState(() => {
    const companyId = localStorage.getItem('companyid');
    const encryptedOrgId = companyId ? encodeURIComponent(CryptoJS.AES.encrypt(companyId, 'ns0-200200221').toString()) : 'your-restaurant-id';
    return { ...defaultConfig, orgId: encryptedOrgId };
  });
  const [isActive, setIsActive] = useState(false);
  const [viewMode, setViewMode] = useState('desktop');
  const [generatedCode, setGeneratedCode] = useState({ snippet: '', iframe: '' });
const [expandedSections, setExpandedSections] = useState({
  general: true,
  colors: false,
  layout: false,
  contactInfo: false,
  reservationSettings: false,
  reservationButton: false,
  embedCode: false,
});
  const [isLoading, setIsLoading] = useState(false);
  const initialConfigRef = useRef(JSON.stringify({ ...defaultConfig, orgId: config.orgId }));

  useEffect(() => {
    const fetchConfig = async () => {
      if (isLoading) return;
      setIsLoading(true);
      const apiURL = process.env.REACT_APP_API_URL || 'https://api.example.com';
      const token = localStorage.getItem('token');
      const companyId = localStorage.getItem('companyid');
      const encryptedOrgId = companyId ? CryptoJS.AES.encrypt(companyId, 'ns0-200200221').toString() : 'your-restaurant-id';
      console.log('WidgetGenerator fetching config for orgId:', encryptedOrgId, 'token:', token || 'none');
      try {
        const response = await axios.get(`${apiURL}/pos/reservations/config`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          params: { orgId: encryptedOrgId },
        });
        console.log('WidgetGenerator config API response:', response.data);
        const fetchedConfig = response.data.config || { ...defaultConfig, orgId: encryptedOrgId };
        setConfig(fetchedConfig);
        setIsActive(response.data.is_active || false);
        initialConfigRef.current = JSON.stringify(fetchedConfig);
        generateCode(fetchedConfig);
        notify('success', 'Configuration loaded successfully');
      } catch (error) {
        console.error('WidgetGenerator error fetching config:', error.message, error.response?.data, error.response?.status);
        if (error.response?.status === 404) {
          setConfig({ ...defaultConfig, orgId: encryptedOrgId });
          setIsActive(false);
          initialConfigRef.current = JSON.stringify({ ...defaultConfig, orgId: encryptedOrgId });
          generateCode({ ...defaultConfig, orgId: encryptedOrgId });
          notify('info', 'No configuration found, using default settings');
        } else {
          setConfig({ ...defaultConfig, orgId: encryptedOrgId });
          setIsActive(false);
          initialConfigRef.current = JSON.stringify({ ...defaultConfig, orgId: encryptedOrgId });
          generateCode({ ...defaultConfig, orgId: encryptedOrgId });
          notify('error', error.response?.data?.error || 'Failed to fetch configuration');
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updatedConfig = {
      ...config,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || 0 : value),
    };
    setConfig(updatedConfig);
    generateCode(updatedConfig);
  };

  const handleSelectChange = (name, value) => {
    const updatedConfig = { ...config, [name]: value };
    setConfig(updatedConfig);
    generateCode(updatedConfig);
  };

  const handleNumberChange = (name, value) => {
    const updatedConfig = { ...config, [name]: value };
    setConfig(updatedConfig);
    generateCode(updatedConfig);
  };

  const handleSliderChange = (name, value) => {
    const updatedConfig = { ...config, [name]: `${value}rem` };
    setConfig(updatedConfig);
    generateCode(updatedConfig);
  };

 const handleColorChange = (color, field) => {
  if (!color || typeof color !== 'object' || !color.hex) return;
  const updatedConfig = {
    ...config,
    colors: { ...config.colors, [field]: color.hex.toUpperCase() },
  };
  setConfig(updatedConfig);
  generateCode(updatedConfig);
};

  const handleContactInfoChange = (value) => {
    const updatedConfig = { ...config, contactInfoContent: value };
    setConfig(updatedConfig);
    generateCode(updatedConfig);
  };

  const generateCode = (values) => {
    const { orgId, colors, font, showLogo } = values;
    const snippet = `
<div id="reservation-widget"></div>
<script src="https://yourdomain.com/reservation-widget.umd.js"
  data-org="${orgId}"
  data-primary="${colors.primary}"
  data-font="${font}"
  data-logo="${showLogo ? logoUrl : ''}"></script>
<script>
  window.ReservationWidget.mount('#reservation-widget', {
    orgId: document.currentScript.getAttribute('data-org'),
    theme: {
      primary: document.currentScript.getAttribute('data-primary'),
      font: document.currentScript.getAttribute('data-font'),
      logo: document.currentScript.getAttribute('data-logo'),
    }
  });
</script>
    `;
    const iframe = `
<iframe
  src="https://yourdomain.com/reservation-widget?org=${orgId}"
  style="border:0; width:100%; min-height:560px;">
</iframe>
    `;
    setGeneratedCode({ snippet, iframe });
  };

  const handleSave = async () => {
    const apiURL = process.env.REACT_APP_API_URL || 'https://api.example.com';
    const token = localStorage.getItem('token');
    const configToSave = { ...config };
    delete configToSave.generatedCode;
    try {
      await axios.post(
        `${apiURL}/pos/reservations/config`,
        { config: configToSave, is_active: isActive },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      initialConfigRef.current = JSON.stringify(configToSave);
      notify('success', 'Settings saved successfully!');
    } catch (error) {
      console.error('WidgetGenerator error saving config:', error.message, error.response?.data, error.response?.status);
      notify('error', error.response?.data?.error || 'Failed to save settings');
    }
  };

  const handleReset = () => {
    const companyId = localStorage.getItem('companyid');
    const encryptedOrgId = companyId ? encodeURIComponent(CryptoJS.AES.encrypt(companyId, 'ns0-200200221').toString()) : 'your-restaurant-id';
    setConfig({ ...defaultConfig, orgId: encryptedOrgId });
    setIsActive(false);
    initialConfigRef.current = JSON.stringify({ ...defaultConfig, orgId: encryptedOrgId });
    generateCode({ ...defaultConfig, orgId: encryptedOrgId });
    notify('success', 'Configuration reset to default!');
  };

  const handleToggleActive = async () => {
    const newActive = !isActive;
    const apiURL = process.env.REACT_APP_API_URL || 'https://api.example.com';
    const token = localStorage.getItem('token');
    const configToSave = { ...config };
    delete configToSave.generatedCode;
    try {
      await axios.post(
        `${apiURL}/pos/reservations/config`,
        { config: configToSave, is_active: newActive },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setIsActive(newActive);
      notify('success', 'Active status updated!');
    } catch (error) {
      console.error('WidgetGenerator error toggling active status:', error.message, error.response?.data, error.response?.status);
      notify('error', error.response?.data?.error || 'Failed to update active status');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    notify('success', 'Copied to clipboard!');
  };

  return (

  <div className="page-wrapper">
    <div className="content container-fluid">
      <div className="page-header page-top-icons">
        <div className="row align-items-center">
          <div className="col-md-6">
            <h1 className="mb-0">Reservation Widget Configuration</h1>
          </div>
          <div className="col-md-6 float-end ms-auto">
            <div className="d-flex justify-content-end">
              <div className="toggle-switch" style={{ transform: 'scale(1.5)' }}>
                <input
                  type="checkbox"
                  id="toggle-isActive"
                  checked={isActive}
                  onChange={handleToggleActive}
                  hidden
                />
                <label htmlFor="toggle-isActive" className="toggle-label">
                  <span className="toggle-inner" />
                  <span className="toggle-circle" />
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          {isActive && (
            <div className="row">
              <div className="col-md-6">
                <GeneralSettings
                  config={config}
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                  handleChange={handleChange}
                  handleSliderChange={handleSliderChange}
                  handleSelectChange={handleSelectChange}
                />
                <ColorSettings
                  config={config}
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                  handleColorChange={handleColorChange}
                />
                <ReservationButtonSettings
                  config={config}
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                  handleChange={handleChange}
                  handleColorChange={handleColorChange}
                />
                <LayoutSettings
                  config={config}
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                  handleChange={handleChange}
                />
                <ContactInfoSettings
                  config={config}
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                  handleContactInfoChange={handleContactInfoChange}
                />
                <ReservationSettings
                  config={config}
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                  handleChange={handleChange}
                  handleSelectChange={handleSelectChange}
                  handleNumberChange={handleNumberChange}
                />
                <EmbedCodeSection
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                  generatedCode={generatedCode}
                  copyToClipboard={copyToClipboard}
                />
                <div className="d-flex justify-content-between">
                  <Button type="primary" onClick={handleSave}>Save Settings</Button>
                  <Button onClick={handleReset}>Reset to Default</Button>
                </div>
              </div>
              <div className="col-md-6" style={{ position: 'sticky', top: '20px', alignSelf: 'flex-start' }}>
                <PreviewSection config={config} viewMode={viewMode} setViewMode={setViewMode} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>

  );
};

export default WidgetGenerator;